import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '@/lib/api'
import type {
  JLocomoBenchmarkSlice,
  JLocomoCurationPacketUpdateRequest,
  JLocomoCurationReviewStatus,
} from '@/types/api'

const CURATION_KEYS = {
  all: ['j-locomo-curation'] as const,
  overview: () => [...CURATION_KEYS.all, 'overview'] as const,
  artifacts: () => [...CURATION_KEYS.all, 'artifacts'] as const,
  readiness: () => [...CURATION_KEYS.all, 'readiness'] as const,
  queue: (limit: number, includeClosed: boolean) =>
    [...CURATION_KEYS.all, 'queue', limit, includeClosed] as const,
  packets: (params: {
    status: string
    slice: string
    split: string
    q: string
    limit: number
    offset: number
  }) => [...CURATION_KEYS.all, 'packets', params] as const,
  packet: (packetId: string) => [...CURATION_KEYS.all, 'packet', packetId] as const,
}

export function useJLocomoCurationOverview() {
  return useQuery({
    queryKey: CURATION_KEYS.overview(),
    queryFn: () => getApiClient().getJLocomoCurationOverview(),
    staleTime: 1000 * 60,
  })
}

export function useJLocomoCurationQueue(limit = 20, includeClosed = false) {
  return useQuery({
    queryKey: CURATION_KEYS.queue(limit, includeClosed),
    queryFn: () => getApiClient().getJLocomoCurationQueue({ limit, includeClosed }),
    staleTime: 1000 * 60,
  })
}

export function useJLocomoArtifactsStatus() {
  return useQuery({
    queryKey: CURATION_KEYS.artifacts(),
    queryFn: () => getApiClient().getJLocomoArtifactsStatus(),
    staleTime: 1000 * 30,
  })
}

export function useJLocomoReadiness() {
  return useQuery({
    queryKey: CURATION_KEYS.readiness(),
    queryFn: () => getApiClient().getJLocomoReadiness(),
    staleTime: 1000 * 30,
  })
}

export function useJLocomoCurationPackets(params: {
  status: JLocomoCurationReviewStatus | 'all'
  slice: JLocomoBenchmarkSlice | 'all'
  split: 'train' | 'eval' | 'all'
  q: string
  limit: number
  offset: number
}) {
  return useQuery({
    queryKey: CURATION_KEYS.packets(params),
    queryFn: () => getApiClient().listJLocomoCurationPackets(params),
    staleTime: 1000 * 30,
  })
}

export function useJLocomoCurationPacket(packetId: string) {
  return useQuery({
    queryKey: CURATION_KEYS.packet(packetId),
    queryFn: () => getApiClient().getJLocomoCurationPacket(packetId),
    enabled: packetId.length > 0,
    staleTime: 1000 * 30,
  })
}

export function useUpdateJLocomoCurationPacket(packetId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: JLocomoCurationPacketUpdateRequest) =>
      getApiClient().updateJLocomoCurationPacket(packetId, input),
    onSuccess: async (response) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.overview() }),
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.all }),
        queryClient.setQueryData(CURATION_KEYS.packet(packetId), response),
      ])
    },
  })
}

export function useValidateJLocomoCuration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (strictPilot: boolean) => getApiClient().validateJLocomoCuration({ strictPilot }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CURATION_KEYS.artifacts() })
    },
  })
}

export function useCompileJLocomoCuration() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => getApiClient().compileJLocomoCuration(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.artifacts() }),
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.overview() }),
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.readiness() }),
      ])
    },
  })
}

export function useRunJLocomoSmokeBenchmark() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { suite: 'recall' | 'answer-quality'; dryRun?: boolean; queryCount?: number }) =>
      getApiClient().runJLocomoSmokeBenchmark(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: CURATION_KEYS.readiness() })
    },
  })
}
